import { Tag } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import React from 'react';

import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'common/hooks/useTranslation';
import type {
  CardStatus,
  CardActivationStatus,
} from 'modules/physical-cards/card';
import {
  getIsCardBlocked,
  getIsCardBlockedByCompletionRule,
  getIsCardBlockedBySchedule,
  getIsCardOrdered,
} from 'modules/physical-cards/utils/card';

import style from './CardOverlay.module.css';

type Props = {
  cardStatus?: CardStatus | null;
  cardState?:
    | CardActivationStatus
    | 'inactive'
    | 'expired'
    | 'cancelled'
    | 'action_required';
  showApprovedStatus?: boolean;
};

const getStatusIcon = ({
  cardStatus,
  cardState,
  showApprovedStatus,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: Props) => {
  if (showApprovedStatus) {
    return 'circle-check';
  }

  if (cardState) {
    if (cardState === 'inactive') {
      return 'circle-pause';
    }

    if (cardState === 'expired' || cardState === 'cancelled') {
      return undefined;
    }

    if (cardState === 'action_required') {
      return 'clock-filled';
    }

    if (getIsCardBlockedBySchedule(cardState)) {
      return 'triangle-warning';
    }

    return getIsCardBlocked(cardState) ||
      getIsCardBlockedByCompletionRule(cardState)
      ? 'lock-close'
      : 'paper-plane';
  }

  if (!cardStatus) {
    return 'clock-outline';
  }

  switch (cardStatus) {
    case 'BLO':
      return 'lock-close';
    default:
      return 'clock-filled';
  }
};

const getCardStateText = (
  cardState:
    | CardActivationStatus
    | 'inactive'
    | 'expired'
    | 'cancelled'
    | 'action_required',
  t: TGlobalFunctionTyped,
) => {
  if (cardState === 'inactive') {
    return t('subscriptions.status.paused');
  }

  if (cardState === 'expired') {
    return t('cards.statuses.expired');
  }

  if (cardState === 'cancelled') {
    return t('cards.statuses.cancelled');
  }

  if (cardState === 'action_required') {
    return t('subscriptions.status.pending');
  }

  if (getIsCardBlocked(cardState)) {
    return t('cards.statuses.blocked');
  }
  if (getIsCardOrdered(cardState)) {
    return t('cards.statuses.ordered');
  }
  if (getIsCardBlockedBySchedule(cardState)) {
    return t('cards.statuses.limited');
  }
  if (getIsCardBlockedByCompletionRule(cardState)) {
    return t('cards.statuses.restricted');
  }
};

const getStatusText = (
  cardStatus: Props['cardStatus'],
  t: TGlobalFunctionTyped,
  showApprovedStatus?: boolean,
) => {
  if (showApprovedStatus) {
    return t('cards.statuses.approved');
  }
  if (!cardStatus) {
    return t('cards.statuses.generating');
  }
  switch (cardStatus) {
    case 'BLO':
      return t('cards.statuses.blocked');
    default:
      return t('cards.statuses.expired');
  }
};

const getTagVariant = ({
  cardStatus,
  cardState,
  showApprovedStatus,
}: Props) => {
  if (showApprovedStatus) {
    return 'success';
  }

  if (cardState) {
    if (cardState === 'inactive') {
      return 'white';
    }
    if (cardState === 'action_required') {
      return 'info';
    }

    if (
      cardState === 'expired' ||
      cardState === 'cancelled' ||
      getIsCardBlockedBySchedule(cardState)
    ) {
      return 'warning';
    }

    return getIsCardBlocked(cardState) ||
      getIsCardBlockedByCompletionRule(cardState)
      ? 'alert'
      : 'info';
  }

  return cardStatus === 'BLO' ? 'alert' : 'white';
};

export const CardInactiveOverlay = (props: Props) => {
  const { t } = useTranslation('global');
  const { showApprovedStatus } = props;

  return (
    <div
      className={classNames(
        'absolute inset-0 z-10 flex justify-end',
        style.background,
      )}
    >
      <div className="inset-0 z-10 mt-16 mr-16 opacity-[1]">
        <Tag variant={getTagVariant(props)} iconName={getStatusIcon(props)}>
          {props.cardState
            ? getCardStateText(props.cardState, t)
            : getStatusText(props.cardStatus, t, showApprovedStatus)}
        </Tag>
      </div>
    </div>
  );
};
